import React from 'react';
import { ExternalLink } from 'lucide-react';
import api from '@/lib/api';

/**
 * LámFon Ecosystem Banners.
 * Fetched dynamically from /api/banners.
 * Admin-managed via the Admin dashboard.
 */

// Hardcoded fallback if API hasn't been seeded yet
const FALLBACK_BANNERS = [
  {
    banner_id: 'lamfonbooks_1',
    title: 'LamFon Books',
    tagline: "Children's books for learning Lamnso'",
    url: 'https://lamfonbooks.com',
    image_url: 'https://static.prod-images.emergentagent.com/jobs/d441dd75-bf9e-4f08-84d6-f230d8bbfb2d/images/d5567a9ed888ade6c4c3d42e414db1221fd95b71535ee576c47807bff2ac2b19.png',
    cta_text: 'Explore Books',
    label: 'From LamFon',
    frequency: 9,
    start_after: 4,
    is_active: true,
  },
];

// Cache fetched banners
let cachedBanners = null;
let cacheTime = 0;

export async function fetchBanners() {
  const now = Date.now();
  if (cachedBanners && now - cacheTime < 300000) return cachedBanners; // 5min cache
  try {
    const res = await api.get('/banners?active_only=true');
    cachedBanners = res.data.banners?.length ? res.data.banners : FALLBACK_BANNERS;
    cacheTime = now;
    return cachedBanners;
  } catch {
    return cachedBanners || FALLBACK_BANNERS;
  }
}

/**
 * Get a banner to insert at a given feed position.
 * Uses the first active banner's frequency settings.
 */
export function getBannerForPosition(position, totalPosts, banners) {
  const activeBanners = banners || FALLBACK_BANNERS;
  if (!activeBanners.length || totalPosts < 3) return null;

  // Use the first banner's settings as the primary frequency
  const primary = activeBanners[0];
  const freq = primary.frequency || 9;
  const startAfter = primary.start_after || 4;

  if (position < startAfter) return null;

  const offset = position - startAfter;
  if (offset >= 0 && offset % freq === 0) {
    const idx = Math.floor(offset / freq) % activeBanners.length;
    return activeBanners[idx];
  }
  return null;
}

/**
 * PromoBanner — inline feed card for LamFon ecosystem content.
 */
export function PromoBanner({ banner }) {
  if (!banner) return null;

  const handleClick = () => {
    if (banner.banner_id) {
      api.post(`/banners/${banner.banner_id}/track-click`).catch(() => {});
    }
  };

  return (
    <a href={banner.url} target="_blank" rel="noopener noreferrer" onClick={handleClick}
      className="block bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`promo-banner-${banner.banner_id}`}
    >
      {banner.image_url && (
        <div className="aspect-[3/2] w-full overflow-hidden bg-[#FDF6F0]">
          <img src={banner.image_url} alt={banner.title}
            className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        <span className="text-[9px] font-bold text-[#D4A373] uppercase tracking-wider">
          {banner.label || 'From LamFon'}
        </span>
        <h3 className="text-sm font-semibold text-[#1C1917] mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {banner.title}
        </h3>
        {banner.tagline && <p className="text-xs text-[#57534E] mt-0.5">{banner.tagline}</p>}
        <div className="flex items-center gap-1 mt-3 text-xs font-semibold text-[#4CAF50]">
          {banner.cta_text || 'Learn More'} <ExternalLink className="w-3 h-3" />
        </div>
      </div>
    </a>
  );
}
